import { useQuery } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { Address } from 'viem';

import { LaunchpadSalePoolType } from '@api';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import {
  BaseTokenDetails,
  DEFAULT_TARGET_CHAIN,
  LAUNCHPAD_DEPOSIT_CONTRACT,
  LAUNCHPAD_PRO_RATA_CONTRACT,
} from '@shared/constants';
import { getToken, GetTokenResult } from '@shared/helpers/web3/getToken';

import { isProRataType } from '@launchpad/utils/contract';

export const getDepositTokenInfo = (token: GetTokenResult): BaseTokenDetails => {
  return {
    address: token.address,
    symbol: token.symbol || 'N/A',
    decimals: token.decimals,
    chainId: DEFAULT_TARGET_CHAIN.id,
    chain: DEFAULT_TARGET_CHAIN,
  };
};

export const getDepositToken = async ({
  type,
  depositContractAddress,
}: {
  type?: LaunchpadSalePoolType;
  depositContractAddress: string | null | undefined;
}): Promise<GetTokenResult> => {
  if (isProRataType(type)) {
    const tokenAddress = await readContract(wagmiConfig, {
      ...LAUNCHPAD_PRO_RATA_CONTRACT,
      chainId: DEFAULT_TARGET_CHAIN.id,
      address: depositContractAddress as Address,
      functionName: 'baseToken',
    });

    return getToken({ tokenAddress });
  }

  const tokenAddress = await readContract(wagmiConfig, {
    ...LAUNCHPAD_DEPOSIT_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    address: depositContractAddress as Address,
    functionName: 'erc20Token',
  });

  return getToken({ tokenAddress });
};

const useDepositTokenQuery = ({
  depositContractAddress,
  type,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
}) => {
  return useQuery({
    queryFn: () => getDepositToken({ type, depositContractAddress }),
    select: (data) => getDepositTokenInfo(data),
    queryKey: ['depositTokenInfo', depositContractAddress, type],
    staleTime: 0,
    enabled: !!depositContractAddress,
  });
};

export default useDepositTokenQuery;
